import { captureError } from "@/shared/services/sentry";

import type { GetChatsParams } from "@/features/chat/domain/repositories/chat";
import type { HttpGateway } from "@/shared/services/http";

export const chatDataSourceImplementation = (gateway: HttpGateway) => ({
  getChats: async <T>(params?: GetChatsParams): Promise<T> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.cursor) queryParams.append("cursor", params.cursor);
      if (params?.limit) queryParams.append("limit", params.limit.toString());
      if (params?.query) queryParams.append("search", params.query);
      if (params?.status) queryParams.append("status", params.status);
      if (params?.last_message_opts)
        queryParams.append("last_message_opts", params.last_message_opts);

      const queryString = queryParams.toString();
      const endpoint = `/v1/chats${queryString ? `?${queryString}` : ""}`;
      return await gateway.get(endpoint, undefined, {
        signal: params?.signal
      });
    } catch (e) {
      // Don't capture error if request was aborted
      if (e instanceof Error && e.name === "AbortError") {
        throw e;
      }
      captureError(e, "chat", "getChats", { params }, "list");
      const error = e as Error;
      throw new Error(
        `Error while fetching chats from /chats: ${error.message}`,
        { cause: e }
      );
    }
  },

  getChatByUserId: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.get(`/v1/chats/${userId}`);
    } catch (e) {
      captureError(e, "chat", "getChatByUserId", { userId }, "list");
      const error = e as Error;
      throw new Error(
        `Error while fetching chat by user from /v1/chats/${userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  getStatusCounts: async <T>(): Promise<T> => {
    try {
      return await gateway.get("/v1/chats/status_counts");
    } catch (e) {
      captureError(e, "chat", "getStatusCounts", undefined, "list");
      const error = e as Error;
      throw new Error(
        `Error while fetching chat status counts: ${error.message}`,
        { cause: e }
      );
    }
  },

  markChatAsRead: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.post(`/v1/chats/${userId}/ack`, {});
    } catch (e) {
      captureError(e, "chat", "markChatAsRead", { userId }, "list");
      const error = e as Error;
      throw new Error(
        `Error while marking chat as read from /v1/chats/${userId}/ack: ${error.message}`,
        { cause: e }
      );
    }
  },

  markChatAsUnread: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.post(`/v1/chats/${userId}/nack`, {});
    } catch (e) {
      captureError(e, "chat", "markChatAsUnread", { userId }, "list");
      const error = e as Error;
      throw new Error(
        `Error while marking chat as unread from /v1/chats/${userId}/nack: ${error.message}`,
        { cause: e }
      );
    }
  },

  muteNotificationsChat: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.patch(`/v1/chats/${userId}`, { is_muted: true });
    } catch (e) {
      captureError(e, "chat", "muteChat", { userId }, "actions");
      const error = e as Error;
      throw new Error(
        `Error while muting chat /v1/chats/${userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  unmuteNotificationsChat: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.patch(`/v1/chats/${userId}`, { is_muted: false });
    } catch (e) {
      captureError(e, "chat", "unmuteChat", { userId }, "actions");
      const error = e as Error;
      throw new Error(
        `Error while unmuting chat /v1/chats/${userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  disableMessaging: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.patch(`/v1/chats/${userId}`, {
        is_enabled: false
      });
    } catch (e) {
      captureError(e, "chat", "disableMessaging", { userId }, "actions");
      const error = e as Error;
      throw new Error(
        `Error while disabling messaging /v1/chats/${userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  enableMessaging: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.patch(`/v1/chats/${userId}`, {
        is_enabled: true
      });
    } catch (e) {
      captureError(e, "chat", "enableMessaging", { userId }, "actions");
      const error = e as Error;
      throw new Error(
        `Error while enabling messaging /v1/chats/${userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  blockUser: async <T>(
    userId: string,
    option: "end_of_period" | "now"
  ): Promise<T> => {
    try {
      return await gateway.post(`/v1/subscriptions/${userId}`, {
        block_schedule: option
      });
    } catch (e) {
      captureError(e, "chat", "blockUser", { userId, option }, "actions");
      const error = e as Error;
      throw new Error(
        `Error while blocking user /v1/subscriptions/${userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  unblockUser: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.post(`/v1/subscriptions/${userId}/unblock`, {});
    } catch (e) {
      captureError(e, "chat", "unblockUser", { userId }, "actions");
      const error = e as Error;
      throw new Error(
        `Error while unblocking user /v1/subscriptions/${userId}/unblock: ${error.message}`,
        { cause: e }
      );
    }
  },

  setNickname: async <T>(
    userId: string,
    nickname: string | null
  ): Promise<T> => {
    try {
      return await gateway.patch(`/v1/users/${userId}`, { nickname });
    } catch (e) {
      captureError(e, "chat", "setNickname", { userId, nickname }, "actions");
      const error = e as Error;
      throw new Error(
        `Error while setting nickname /v1/users/${userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  getUserById: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.get(`/v1/users/${userId}`);
    } catch (e) {
      captureError(e, "chat", "getUserById", { userId }, "list");
      const error = e as Error;
      throw new Error(
        `Error while fetching user from /v1/users/${userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  // Care about this endpoint, cache in front implemented but we need a real strategie about it
  getTotalSpent: async <T>(userId: string): Promise<T> => {
    try {
      return await gateway.get(`/v1/users/${userId}/total_spent`);
    } catch (e) {
      captureError(e, "chat", "getTotalSpent", { userId }, "list");
      const error = e as Error;
      throw new Error(
        `Error while fetching total spent from /v1/users/${userId}/total_spent: ${error.message}`,
        { cause: e }
      );
    }
  }
});
